import { useCallback, useEffect, useRef, useState } from 'react';
import { Tariff } from 'interfaces';
import {
  applyTimeToDateTime,
  calculateAnesthesiaUnitsFromTimes,
  determineAssistPercentage,
  generateNewRowItem,
  getDefaultTariffServiceEnd,
  getTariffServiceTimes,
  mapTariffsToRows,
  RowItem,
} from './utils/tariffHelpers';
import { UseFormReturnType } from '@mantine/form';
import { DEFAULT_CLAIM_TARIFF_STATUS } from '@billing/validation/tariff';
import { useCurrentSiteStore } from 'store';
import { nowISO } from 'utils/dateUtils';
import { isAnesthesiaSpecialty } from '@billing/validation/specialty';
import type { Province } from '@billing/validation/payor';

interface UseTariffRowStateArgs {
  initialTariffs?: RowItem[];
  claimServiceStart?: string;
  claimServiceEnd?: string;
  specialtyCode?: string;
  province?: Province | null;
  specialtyLookup?: Array<{ code: string; description: string }>;
  hospitalCode?: string;
  serviceLocationIndicator?: string | null;
  defaultICD9?: string | null;
  tariffPath?: string;
  form: UseFormReturnType<any>;
  onDirty: () => void;
}

export const useTariffRowState = ({
  initialTariffs = [],
  claimServiceStart,
  claimServiceEnd,
  specialtyCode,
  province,
  specialtyLookup = [],
  hospitalCode,
  serviceLocationIndicator,
  defaultICD9,
  tariffPath,
  form,
  onDirty,
}: UseTariffRowStateArgs) => {
  const { timezone } = useCurrentSiteStore();

  const isAnesthesiaAutoApplicable = useCallback(
    (tariff: Pick<Tariff, 'anesthesiaServiceValue'>) => {
      const anesthesiaServiceValue = Number(tariff.anesthesiaServiceValue);
      return (
        isAnesthesiaSpecialty(province, specialtyCode) &&
        Number.isFinite(anesthesiaServiceValue) &&
        anesthesiaServiceValue !== 0
      );
    },
    [province, specialtyCode]
  );

  const getAutoAnesthesiaUnits = useCallback(
    (serviceStart?: string | null, serviceEnd?: string | null) =>
      calculateAnesthesiaUnitsFromTimes(serviceStart, serviceEnd),
    []
  );

  const resolveHydratedAnesthesiaUnits = useCallback(
    (item: RowItem, persistedUnits?: number | null): RowItem => {
      if (!isAnesthesiaAutoApplicable(item)) {
        return {
          ...item,
          anesthesiaUnits: Number(item.anesthesiaUnits ?? 0),
          isAnesthesiaUnitsManual: false,
        };
      }

      const calculatedUnits = getAutoAnesthesiaUnits(
        item.serviceStart,
        item.serviceEnd
      );
      const normalizedPersistedUnits = Number(persistedUnits);
      const hasPersistedUnits =
        persistedUnits != null &&
        Number.isFinite(normalizedPersistedUnits) &&
        (normalizedPersistedUnits > 0 || item.isAnesthesiaUnitsManual);
      const isPersistedOverride =
        hasPersistedUnits && normalizedPersistedUnits !== calculatedUnits;
      const useOverride = item.isAnesthesiaUnitsManual || isPersistedOverride;
      const overrideUnits = hasPersistedUnits
        ? normalizedPersistedUnits
        : Number(item.anesthesiaUnits ?? 0);

      return {
        ...item,
        anesthesiaUnits: useOverride ? overrideUnits : calculatedUnits,
        isAnesthesiaUnitsManual: useOverride,
      };
    },
    [getAutoAnesthesiaUnits, isAnesthesiaAutoApplicable]
  );

  const initialRowsRef = useRef<RowItem[] | null>(null);
  if (!initialRowsRef.current) {
    initialRowsRef.current = mapTariffsToRows(
      initialTariffs,
      claimServiceStart,
      claimServiceEnd
    ).map((item, index) =>
      resolveHydratedAnesthesiaUnits(
        item,
        initialTariffs[index]?.anesthesiaUnits
      )
    );
  }

  const [items, setItems] = useState<RowItem[]>(initialRowsRef.current);
  const itemsRef = useRef(items);

  useEffect(() => {
    itemsRef.current = items;
    form.validate();
  }, [items]);

  const syncServiceStartFromClaim = useCallback(
    (serviceStart?: string) => {
      if (!serviceStart) return;

      let didChange = false;
      setItems((prev) =>
        prev.map((item) => {
          if (item.serviceStartOverridden) return item;
          if (item.serviceStart === serviceStart) return item;

          didChange = true;
          const nextItem = {
            ...item,
            serviceStart,
            anesthesiaUnits:
              isAnesthesiaAutoApplicable(item) && !item.isAnesthesiaUnitsManual
                ? getAutoAnesthesiaUnits(serviceStart, item.serviceEnd)
                : item.anesthesiaUnits,
          } as RowItem;

          return nextItem;
        })
      );

      if (didChange) {
        onDirty();
      }
    },
    [getAutoAnesthesiaUnits, isAnesthesiaAutoApplicable, onDirty]
  );

  const syncServiceEndFromClaim = useCallback(
    (serviceEnd?: string) => {
      if (!serviceEnd) return;

      let didChange = false;
      setItems((prev) =>
        prev.map((item) => {
          if (item.serviceEndOverridden) return item;
          if (item.serviceEnd === serviceEnd) return item;

          didChange = true;
          const nextItem = {
            ...item,
            serviceEnd,
            anesthesiaUnits:
              isAnesthesiaAutoApplicable(item) && !item.isAnesthesiaUnitsManual
                ? getAutoAnesthesiaUnits(item.serviceStart, serviceEnd)
                : item.anesthesiaUnits,
          } as RowItem;

          return nextItem;
        })
      );

      if (didChange) {
        onDirty();
      }
    },
    [getAutoAnesthesiaUnits, isAnesthesiaAutoApplicable, onDirty]
  );

  const hydrateRows = useCallback(
    (tariffs: RowItem[] = []) => {
      const nextItems = mapTariffsToRows(
        tariffs,
        claimServiceStart,
        claimServiceEnd,
        itemsRef.current
      ).map((item, index) =>
        resolveHydratedAnesthesiaUnits(item, tariffs[index]?.anesthesiaUnits)
      );

      itemsRef.current = nextItems;
      setItems(nextItems);
    },
    [claimServiceEnd, claimServiceStart, resolveHydratedAnesthesiaUnits]
  );

  const handleStartTimeChange = useCallback(
    (rowID: string, timeValue: string) => {
      let didChange = false;

      setItems((prev) =>
        prev.map((item) => {
          if (item._rowID !== rowID) return item;

          const nextServiceStart = applyTimeToDateTime(
            item.serviceStart || nowISO(timezone),
            timeValue,
            timezone
          );
          if (!nextServiceStart || nextServiceStart === item.serviceStart) {
            return item;
          }

          didChange = true;
          const shouldSyncServiceEnd = !item.serviceEndOverridden;
          const nextServiceEnd = shouldSyncServiceEnd
            ? getDefaultTariffServiceEnd(nextServiceStart)
            : item.serviceEnd;

          return {
            ...item,
            serviceStart: nextServiceStart,
            serviceEnd: nextServiceEnd,
            serviceStartOverridden: true,
            anesthesiaUnits:
              isAnesthesiaAutoApplicable(item) && !item.isAnesthesiaUnitsManual
                ? getAutoAnesthesiaUnits(nextServiceStart, nextServiceEnd)
                : item.anesthesiaUnits,
            isDirty: true,
          };
        })
      );

      if (didChange) {
        onDirty();
      }
    },
    [getAutoAnesthesiaUnits, isAnesthesiaAutoApplicable, onDirty]
  );

  const handleEndTimeChange = useCallback(
    (rowID: string, timeValue: string) => {
      let didChange = false;

      setItems((prev) =>
        prev.map((item) => {
          if (item._rowID !== rowID) return item;

          const nextServiceEnd = applyTimeToDateTime(
            item.serviceEnd ||
              getDefaultTariffServiceEnd(item.serviceStart || nowISO(timezone)),
            timeValue,
            timezone
          );
          if (!nextServiceEnd || nextServiceEnd === item.serviceEnd) {
            return item;
          }

          didChange = true;
          return {
            ...item,
            serviceEnd: nextServiceEnd,
            serviceEndOverridden: true,
            anesthesiaUnits:
              isAnesthesiaAutoApplicable(item) && !item.isAnesthesiaUnitsManual
                ? getAutoAnesthesiaUnits(item.serviceStart, nextServiceEnd)
                : item.anesthesiaUnits,
            isDirty: true,
          };
        })
      );

      if (!didChange) return;
      onDirty();
    },
    [getAutoAnesthesiaUnits, isAnesthesiaAutoApplicable, onDirty]
  );

  const addBlankRow = useCallback((): string => {
    const lastValid =
      [...items].reverse().find((item) => item.icd9) ||
      items[items.length - 1] ||
      null;
    const newItem = generateNewRowItem({
      last: lastValid,
      claimServiceStart,
      claimServiceEnd,
      tariffs: items,
    });
    setItems((prev) => [
      ...prev,
      { ...newItem, icd9: defaultICD9 || newItem.icd9 },
    ]);
    onDirty();
    return newItem._rowID;
  }, [claimServiceEnd, claimServiceStart, defaultICD9, items, onDirty]);

  const handleDeleteTariff = useCallback(
    (rowID: string) => {
      let didChange = false;
      setItems((prev) => {
        const next = prev.filter((item) => item._rowID !== rowID);
        didChange = next.length !== prev.length;
        return didChange ? next : prev;
      });
      if (didChange) {
        onDirty();
      }
    },
    [onDirty]
  );

  const handleUnitsChange = useCallback(
    (rowID: string, newUnits: number) => {
      const parsedUnits = Number(newUnits);
      const safeUnits =
        !Number.isFinite(parsedUnits) || parsedUnits < 1 ? 1 : parsedUnits;
      let didChange = false;

      setItems((prev) => {
        const next = prev.map((item) => {
          if (item._rowID !== rowID) return item;

          const nextTotal =
            item.isManualTotal && item.total != null ? item.total : undefined;
          if (item.units === safeUnits && item.total === nextTotal) {
            return item;
          }

          didChange = true;
          return {
            ...item,
            units: safeUnits,
            total: nextTotal,
          };
        });

        return didChange ? next : prev;
      });

      if (didChange) {
        onDirty();
      }
    },
    [onDirty]
  );

  const handlePercentageChange = useCallback(
    (rowID: string, newPct: number | undefined) => {
      const normalizedPct = newPct == null ? undefined : Number(newPct);
      const safePct =
        normalizedPct == null || Number.isNaN(normalizedPct)
          ? undefined
          : normalizedPct;
      let didChange = false;

      setItems((prev) => {
        const next = prev.map((item) => {
          if (item._rowID !== rowID) return item;

          const nextTotal =
            item.isManualTotal && item.total != null ? item.total : undefined;
          if (item.percentage === safePct && item.total === nextTotal) {
            return item;
          }

          didChange = true;
          return {
            ...item,
            percentage: safePct,
            total: nextTotal,
          };
        });

        return didChange ? next : prev;
      });

      if (didChange) {
        onDirty();
      }
    },
    [onDirty]
  );

  const handleSplitChange = useCallback(
    (rowID: string, split: string) => {
      let didChange = false;
      setItems((prev) => {
        const next = prev.map((item) => {
          if (item._rowID !== rowID || item.split === split) return item;
          didChange = true;
          return { ...item, split };
        });
        return didChange ? next : prev;
      });
      if (didChange) {
        onDirty();
      }
    },
    [onDirty]
  );

  const handleAnesthesiaUnitsChange = useCallback(
    (rowID: string, newUnits: number) => {
      const parsed = Number(newUnits);
      const safeUnits =
        !Number.isFinite(parsed) || parsed < 0
          ? 0
          : Math.min(96, Math.floor(parsed));
      let didChange = false;
      setItems((prev) => {
        const next = prev.map((item) => {
          if (item._rowID !== rowID) return item;
          if (
            item.anesthesiaUnits === safeUnits &&
            item.isAnesthesiaUnitsManual
          )
            return item;
          didChange = true;
          return {
            ...item,
            anesthesiaUnits: safeUnits,
            isAnesthesiaUnitsManual: true,
          };
        });
        return didChange ? next : prev;
      });
      if (didChange) onDirty();
    },
    [onDirty]
  );

  const handleIncisionChange = useCallback(
    (rowID: string, incision: string | null) => {
      let didChange = false;
      setItems((prev) => {
        const next = prev.map((item) => {
          if (item._rowID !== rowID || item.incision === incision) return item;
          didChange = true;
          return { ...item, incision };
        });
        return didChange ? next : prev;
      });
      if (didChange) {
        onDirty();
      }
    },
    [onDirty]
  );

  const handleICD9Change = useCallback(
    (rowID: string, icd9: string) => {
      let didChange = false;
      let rowIndex = -1;

      setItems((prev) => {
        rowIndex = prev.findIndex((item) => item._rowID === rowID);
        const next = prev.map((item) => {
          if (item._rowID !== rowID || item.icd9 === icd9) return item;
          didChange = true;
          return { ...item, icd9 };
        });
        return didChange ? next : prev;
      });
      if (didChange) {
        if (form && tariffPath && rowIndex !== -1) {
          form.setFieldValue(`${tariffPath}.${rowIndex}.icd9`, icd9);
          form.validateField(tariffPath);
        }
        onDirty();
      }
    },
    [form, onDirty, tariffPath]
  );

  const handleTariffSelection = useCallback(
    (newTariff: Tariff, targetRowID: string) => {
      const percentage =
        newTariff.prefix === 0
          ? determineAssistPercentage(specialtyCode || '', specialtyLookup)
          : newTariff.percentage ?? 100;

      setItems((prev) => {
        const idx = prev.findIndex((row) => row._rowID === targetRowID);
        if (idx === -1) return prev;

        const rowToUpdate = prev[idx];
        const updatedRow = {
          _rowID: rowToUpdate._rowID,
          ...newTariff,
          originalRate: newTariff.rate,
          units: newTariff.units ?? 1,
          icd9: rowToUpdate.icd9 || newTariff.icd9 || '',
          split: newTariff.split || ' ',
          incision: rowToUpdate.incision ?? newTariff.incision ?? null,
          anesthesiaUnits: isAnesthesiaAutoApplicable(newTariff)
            ? getAutoAnesthesiaUnits(
                rowToUpdate.serviceStart,
                rowToUpdate.serviceEnd
              )
            : Number(
                rowToUpdate.anesthesiaUnits ?? newTariff.anesthesiaUnits ?? 0
              ),
          isAnesthesiaUnitsManual: false,
          percentage,
          // Leave `total` unset so the downstream tariff math can recompute the
          // correct amount with location, hospital, and override rules.
          total: undefined,
          isManualTotal: false,
          isDirty: false,
          status:
            rowToUpdate.status ||
            newTariff.status ||
            DEFAULT_CLAIM_TARIFF_STATUS,
          serviceStartOverridden: rowToUpdate.serviceStartOverridden,
          serviceEndOverridden: rowToUpdate.serviceEndOverridden,
          ...getTariffServiceTimes(
            rowToUpdate,
            claimServiceStart,
            claimServiceEnd
          ),
        } as RowItem;

        const next = [...prev];
        next[idx] = updatedRow;
        return next;
      });
      onDirty();
    },
    [
      claimServiceStart,
      claimServiceEnd,
      getAutoAnesthesiaUnits,
      hospitalCode,
      isAnesthesiaAutoApplicable,
      onDirty,
      serviceLocationIndicator,
      specialtyCode,
      specialtyLookup,
    ]
  );

  const handleManualTotalSave = useCallback(
    (rowID: string, value: number | undefined) => {
      if (value == null || Number.isNaN(value)) {
        return false;
      }

      setItems((prev) =>
        prev.map((item) =>
          item._rowID === rowID
            ? {
                ...item,
                total: Number(Number(value).toFixed(2)),
                isManualTotal: true,
              }
            : item
        )
      );
      onDirty();
      return true;
    },
    [onDirty]
  );

  const handleManualTotalReset = useCallback(
    (rowID: string) => {
      let didChange = false;
      setItems((prev) => {
        const next = prev.map((item) => {
          if (item._rowID !== rowID) return item;
          if (!item.isManualTotal && item.total == null) {
            return item;
          }

          didChange = true;
          return {
            ...item,
            isManualTotal: false,
            total: undefined,
          };
        });
        return didChange ? next : prev;
      });
      if (didChange) {
        onDirty();
      }
    },
    [onDirty]
  );

  return {
    items,
    setItems,
    hydrateRows,
    syncServiceStartFromClaim,
    syncServiceEndFromClaim,
    handleDeleteTariff,
    handleUnitsChange,
    handlePercentageChange,
    handleSplitChange,
    handleAnesthesiaUnitsChange,
    handleIncisionChange,
    handleICD9Change,
    handleStartTimeChange,
    handleEndTimeChange,
    handleTariffSelection,
    handleManualTotalSave,
    handleManualTotalReset,
    addBlankRow,
  };
};
